import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../api/client';
import { useSession } from '../auth/session';
import { Alert, Button, Chip, Empty, Note, PageHeader, Panel, Spinner, Table, Tabs } from '../ui';
import { invalidateAll } from '../api/write-keys';

/**
 * Notifications — Admin's view of BRD §6.17: which types exist and who receives them (NT-06),
 * each channel switched on or off (NT-04), and every delivery, failures first (NT-05).
 *
 * Deliberately NOT here in v1:
 *   - a template editor (NT-03). Templates are code; this screen shows what each type says by
 *     describing it, and a wording change is a reviewed code change until Miraki asks to edit.
 *   - the in-app centre itself (NT-02). That belongs in the app shell for every user, not on an
 *     Admin screen; its API exists (`/me/notifications`, `/shop/notifications`).
 *
 * Gated on `notification.manage`.
 */

type Channel = 'EMAIL' | 'IN_APP';
interface TypeRow {
  key: string; matrix: string; description: string;
  audiences: Array<{ side: 'RETAILER' | 'INTERNAL'; permission: string }>;
  channels: Array<{ channel: Channel; enabled: boolean; isDefault: boolean; byDefault: boolean }>;
}
interface Delivery {
  id: string; channel: Channel; status: 'SENT' | 'FAILED' | 'DELIVERED';
  recipientKind: 'RETAILER' | 'INTERNAL'; recipientEmail: string; error: string | null;
  attempts: number; attemptedAt: string; type: string; title: string;
}
type RunResult = { skipped?: string; today?: string; results: Array<{ retailerId: string; offset: number; result: { status: string } }> };

const CHANNEL_LABEL: Record<Channel, string> = { EMAIL: 'Email', IN_APP: 'In-app' };
const STATUS_TONE: Record<Delivery['status'], 'done' | 'stop' | 'us'> = { SENT: 'done', FAILED: 'stop', DELIVERED: 'us' };

export function Notifications() {
  const { portal } = useSession();
  const qc = useQueryClient();
  const [tab, setTab] = useState('types');
  const [status, setStatus] = useState<'FAILED' | 'ALL'>('FAILED');
  const [failure, setFailure] = useState<string | null>(null);
  const [run, setRun] = useState<RunResult | null>(null);

  const types = useQuery({ queryKey: ['notifications', 'types'], queryFn: () => api.get<TypeRow[]>('/notifications/types') });
  const deliveries = useQuery({
    queryKey: ['notifications', 'deliveries', status],
    queryFn: () => api.get<Delivery[]>(`/notifications/deliveries${status === 'FAILED' ? '?status=FAILED' : ''}`),
  });

  const act = (fn: () => Promise<unknown>) => {
    setFailure(null);
    void (async () => {
      try { await fn(); await invalidateAll(qc, [['notifications']]); } catch (e) {
        setFailure(e instanceof ApiError ? e.message : 'That did not work.');
      }
    })();
  };

  if (types.isLoading) return <Spinner />;
  if (types.error) return <Alert tone="stop">{types.error.message}</Alert>;

  return (
    <>
      <PageHeader
        title="Notifications"
      />
      {failure ? <Alert tone="stop">{failure}</Alert> : null}
      <Tabs active={tab} onChange={setTab} tabs={[
        { id: 'types', label: 'Types and channels' },
        { id: 'log', label: 'Delivery log', ...(status === 'FAILED' && deliveries.data?.length ? { badge: String(deliveries.data.length) } : {}) },
      ]} />

      {tab === 'types' ? (
        <Panel pad>
          <Note>
            Who receives a notice is decided by permission, not by name: a fee or payable notice goes to the
            people who may see payables, so a Buyer is never told what their own screen hides (NT-06, DB-07).
            Wording is fixed in v1 — Admin switches channels, not text (NT-03).
          </Note>
          <Table<TypeRow>
            rows={types.data ?? []}
            rowKey={(t) => t.key}
            columns={[
              { header: 'Notification', render: (t) => <><b>{t.description}</b><div className="mk-hint">{t.matrix}</div></> },
              {
                header: 'Who receives it',
                render: (t) => t.audiences.map((a) => (
                  <div key={`${a.side}${a.permission}`}>
                    {a.side === 'RETAILER' ? 'Retailer users with ' : 'Staff with '}<code>{a.permission}</code>
                  </div>
                )),
              },
              ...(['EMAIL', 'IN_APP'] as const).map((c) => ({
                header: CHANNEL_LABEL[c],
                render: (t: TypeRow) => {
                  const ch = t.channels.find((x) => x.channel === c)!;
                  return (
                    <label style={{ display: 'flex', gap: 'var(--mk-space-2)', alignItems: 'center' }}>
                      <input
                        type="checkbox" checked={ch.enabled} style={{ width: 'auto', margin: 0 }}
                        onChange={(e) => act(() => api.put(`/notifications/types/${t.key}/channels/${c}`, { enabled: e.target.checked }))}
                      />
                      <span className="mk-hint">{ch.isDefault ? 'default' : `set (default ${ch.byDefault ? 'on' : 'off'})`}</span>
                    </label>
                  );
                },
              })),
            ]}
          />
          <div style={{ marginTop: 'var(--mk-space-4)' }}>
            <Button tone="ghost" onClick={() => act(async () => setRun(await api.post<RunResult>('/notifications/run/subscription-reminders')))}>
              Send due renewal reminders for {portal?.name ?? 'this portal'} now
            </Button>
            <div className="mk-hint">
              Safe to press twice: a reminder already sent is never sent again. The daily schedule runs the same thing.
            </div>
            {run ? (
              <Note>
                {run.skipped ?? (run.results.length === 0 ? 'Nothing was due.'
                  : run.results.map((r) => `${r.offset === 0 ? 'today' : `${r.offset}-day`}: ${r.result.status.toLowerCase()}`).join(' · '))}
              </Note>
            ) : null}
          </div>
        </Panel>
      ) : (
        <Panel
          pad
          actions={(
            <Button size="sm" tone="ghost" onClick={() => setStatus(status === 'FAILED' ? 'ALL' : 'FAILED')}>
              {status === 'FAILED' ? 'Show every delivery' : 'Show failures only'}
            </Button>
          )}
        >
          {deliveries.isLoading ? <Spinner /> : (deliveries.data?.length ?? 0) === 0 ? (
            <Empty title={status === 'FAILED' ? 'No failed deliveries' : 'Nothing sent yet'} body="Every delivery attempt is listed here (NT-05)." />
          ) : (
            <Table<Delivery>
              rows={deliveries.data ?? []}
              rowKey={(d) => d.id}
              columns={[
                { header: 'When', render: (d) => d.attemptedAt.slice(0, 16).replace('T', ' ') },
                { header: 'Notice', render: (d) => d.title },
                { header: 'To', render: (d) => <>{d.recipientEmail} <span className="mk-dim">{d.recipientKind === 'RETAILER' ? 'retailer' : 'staff'}</span></> },
                { header: 'Channel', render: (d) => CHANNEL_LABEL[d.channel] },
                {
                  header: 'Result',
                  render: (d) => (
                    <>
                      <Chip tone={STATUS_TONE[d.status]}>{d.status.toLowerCase()}</Chip>
                      {d.attempts > 1 ? <span className="mk-hint"> after {d.attempts} attempts</span> : null}
                      {d.error ? <div className="mk-hint">{d.error}</div> : null}
                    </>
                  ),
                },
                {
                  header: '', numeric: true,
                  render: (d) => (d.status === 'FAILED' && d.channel === 'EMAIL'
                    ? <Button size="sm" tone="ghost" onClick={() => act(() => api.post(`/notifications/deliveries/${d.id}/retry`))}>Retry</Button>
                    : null),
                },
              ]}
            />
          )}
        </Panel>
      )}
    </>
  );
}
